import React, { ReactNode, forwardRef, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { FieldProps } from 'formik';
import { DateTime } from 'luxon';
import { isEqual, isPlainObject } from 'lodash';

import { Box, Chip, Grid, TextFieldProps, Typography, useTheme } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import { DateValidationError } from '@mui/x-date-pickers/models';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';

import { Button } from 'components/button/Button';
import { ErrorMessage } from 'components/util/ErrorMessage';
import {
  DATE_FORMAT,
  IDays,
  INVALID_DATE,
  YEAR_MONTH_DATE_FORMAT,
  formatDate,
  isValidDate,
  minusRelativeDays,
  plusRelativeDays,
  relativeToday,
  toDateTime,
} from 'utils/dates';
import { inErrorState } from 'utils/form';
import { Env } from 'config/env';

type OmittedMuiProps = 'value' | 'onChange' | 'defaultValue';

export interface DatePickerProps
  extends Omit<MuiDatePickerProps<DateTime>, OmittedMuiProps>,
    FieldProps {
  /**
   * Format string used to display and parse the date in the input.
   */
  dateFormat?: string;
  /**
   * Set it true, if the datepicker is used to input intervals.
   */
  isInterval?: boolean;
  /**
   * TextField variant used for the picker input.
   */
  variant?: TextFieldProps['variant'];
  /**
   * Alias of `variant` kept for backwards compatibility.
   */
  inputVariant?: TextFieldProps['variant'];
  /**
   * Callback fired when the value (the selected date) changes.
   */
  onChange?: (value: DateTime | null) => void;
  /**
   * Set it true, if you want to disable the relative date Chips.
   */
  isRelativeDateDisabled?: boolean;
  /**
   * Set it true, if you want to display the year and month only.
   */
  isYearAndMonthOnly?: boolean;
  /**
   * Parent form key used to prune empty interval objects.
   */
  rootKey?: string;
  /**
   * Marks the interval as invalid (from is after to).
   */
  badInterval?: boolean;
  label?: ReactNode;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  id?: string;
}

export const styles = {
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingX: 3,
    paddingBottom: 2,
  },
  chipContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: 3,
    pb: 3,
  },
  customChip: {
    width: 80,
  },
  popperProps: {
    width: '350px',
    '.MuiPickersLayout-root': {
      display: 'flex',
      flexDirection: 'column',
    },
    '.MuiDateCalendar-viewTransitionContainer': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
};

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      field,
      form,
      meta: _meta,
      minDate,
      maxDate,
      required,
      isYearAndMonthOnly = false,
      dateFormat,
      variant,
      inputVariant,
      fullWidth = true,
      rootKey,
      onChange: onChangeDefault,
      onError,
      placeholder,
      badInterval = false,
      isInterval = false,
      isRelativeDateDisabled = false,
      label,
      id,
      ...props
    },
    ref
  ) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const pickerError = useRef<DateValidationError>(null);

    const resolvedVariant = variant ?? inputVariant ?? Env.DEFAULT_INPUT_VARIANT;
    const resolvedFormat =
      dateFormat ?? (isYearAndMonthOnly ? YEAR_MONTH_DATE_FORMAT : DATE_FORMAT);

    useEffect(() => {
      if (badInterval) {
        form.setFieldValue('badInterval', false);
      } else if ('badInterval' in (form.values as Record<string, unknown>)) {
        delete (form.values as Record<string, unknown>).badInterval;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [badInterval]);

    useEffect(() => {
      if (!rootKey) return;
      const parent = (form.values as Record<string, unknown>)[rootKey];
      if (isPlainObject(parent) && Object.keys(parent as object).length === 0) {
        delete (form.values as Record<string, unknown>)[rootKey];
      }
    }, [form.values, rootKey]);

    const commitDate = (date: DateTime | null) => {
      if (date === null) {
        form.setFieldValue(field.name, undefined, true);
        return;
      }

      if (!isValidDate(date)) {
        form.setFieldValue(field.name, INVALID_DATE, true);
        return;
      }

      const formatted = isYearAndMonthOnly
        ? `${formatDate(date, 'yyyy-MM', { utc: false })}-01`
        : formatDate(date, 'yyyy-MM-dd', { utc: false });

      form.setFieldValue(field.name, formatted, true);
      onChangeDefault?.(date);

      if (!isYearAndMonthOnly) {
        setIsOpen(false);
      }
    };

    const relativeChangeHandler = (dayObj: IDays) => {
      form.setFieldValue(field.name, dayObj, true);
      setIsOpen(false);
    };

    const onClear = () => {
      form.setFieldValue(field.name, null, true);
      setIsOpen(false);
    };

    const handleError: MuiDatePickerProps<DateTime>['onError'] = (reason, value) => {
      pickerError.current = reason;
      onError?.(reason, value);
    };

    const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.currentTarget.contains(event.relatedTarget)) return;

      if (pickerError.current === 'invalidDate' && field.value !== INVALID_DATE) {
        form.setFieldTouched(field.name, true, false);
        form.setFieldValue(field.name, INVALID_DATE, true);
        return;
      }

      form.setFieldTouched(field.name, true, true);
    };

    const chipColor = (isSelected: boolean) => (isSelected ? 'primary' : 'secondary');

    const CustomActionBar = (_actionBarProps: PickersActionBarProps) => (
      <>
        {isInterval && !isRelativeDateDisabled && (
          <>
            <Box sx={styles.textContainer}>
              <Trans i18nKey="DATE.RELATIVE_DATE" />
            </Box>
            <Box sx={styles.chipContainer}>
              <Chip
                color={chipColor(isEqual(field.value, minusRelativeDays))}
                label={
                  <Typography variant="subtitle2">
                    <Trans i18nKey="DATE.SUBSTRACT_TEN_DAYS" />
                  </Typography>
                }
                onClick={() => relativeChangeHandler(minusRelativeDays)}
                sx={styles.customChip}
              />
              <Chip
                color={chipColor(isEqual(field.value, relativeToday))}
                label={
                  <Typography variant="subtitle2">
                    <Trans i18nKey="DATE.TODAY" />
                  </Typography>
                }
                onClick={() => relativeChangeHandler(relativeToday)}
                sx={styles.customChip}
              />
              <Chip
                color={chipColor(isEqual(field.value, plusRelativeDays))}
                label={
                  <Typography variant="subtitle2">
                    <Trans i18nKey="DATE.ADD_TEN_DAYS" />
                  </Typography>
                }
                onClick={() => relativeChangeHandler(plusRelativeDays)}
                sx={styles.customChip}
              />
            </Box>
          </>
        )}
        {isYearAndMonthOnly && (
          <Grid container spacing={1} p={1}>
            <Grid item sm={10}>
              <Button onClick={() => setIsOpen(false)} fullWidth>
                <Trans i18nKey="COMMON.OK" />
              </Button>
            </Grid>
            <Grid item sm={2}>
              <Button color="error" onClick={onClear} buttonSx={{ minWidth: 0 }}>
                x
              </Button>
            </Grid>
          </Grid>
        )}
      </>
    );

    const value = toDateTime(field.value);
    const showError = inErrorState(form, field);

    return (
      <Box data-testid="date-picker">
        <MuiDatePicker
          {...props}
          ref={ref}
          value={value}
          onChange={commitDate}
          onError={handleError}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          closeOnSelect={!isYearAndMonthOnly}
          minDate={minDate}
          maxDate={maxDate}
          desktopModeMediaQuery={theme.breakpoints.up('sm')}
          format={resolvedFormat}
          views={isYearAndMonthOnly ? ['year', 'month'] : undefined}
          slots={{ actionBar: CustomActionBar }}
          slotProps={{
            popper: {
              sx: styles.popperProps,
              placement: isYearAndMonthOnly ? 'bottom' : 'right',
            },
            textField: {
              id,
              name: field.name,
              margin: Env.DEFAULT_INPUT_MARGIN,
              onBlur,
              label,
              required,
              fullWidth,
              variant: resolvedVariant,
              placeholder,
              error: showError,
              inputProps: { spellCheck: false, autoCorrect: 'off', autoCapitalize: 'off' },
            },
          }}
        />
        <ErrorMessage variant={resolvedVariant} show form={form} field={field} />
      </Box>
    );
  }
);

DatePicker.displayName = 'DatePicker';
